import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, CalendarDays, GraduationCap, TrendingUp, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import { useFormations, useSessions, useStatsFormations } from '@/hooks/queries/useFormation';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { SessionStatut } from '@/lib/database.types';

export function FormationDashboardPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);

  const { data: formations = [] } = useFormations({ sousSegmentIds: segmentIds, statut: 'actif' });
  const { data: stats = [] } = useStatsFormations(eurlKostGroup?.id ?? null);
  const next7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const { data: upcomingSessions = [] } = useSessions({
    dateFrom: today,
    dateTo: next7,
  });

  const totals = useMemo(() => {
    const t = { ca_realise: 0, ca_engage: 0, nb_inscriptions: 0, nps_sum: 0, nps_count: 0 };
    for (const s of stats) {
      t.ca_realise += Number(s.ca_realise);
      t.ca_engage += Number(s.ca_engage);
      t.nb_inscriptions += Number(s.nb_inscriptions_actives);
      if (s.nps_moyen != null) {
        t.nps_sum += Number(s.nps_moyen) * Number(s.nb_evaluations);
        t.nps_count += Number(s.nb_evaluations);
      }
    }
    return t;
  }, [stats]);
  const npsMoyen = totals.nps_count > 0 ? totals.nps_sum / totals.nps_count : null;

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('formation.title')}
        subtitle={t('formation.subtitle')}
        actions={<SelecteurSousSegmentFormation value={segment} onChange={setSegment} />}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={<BookOpen className="h-4 w-4" />}
          label="Formations actives"
          value={String(formations.length)}
        />
        <KpiCard
          icon={<CalendarDays className="h-4 w-4" />}
          label="Sessions à venir 7j"
          value={String(upcomingSessions.length)}
        />
        <KpiCard
          icon={<Users className="h-4 w-4" />}
          label="Inscriptions actives"
          value={String(totals.nb_inscriptions)}
        />
        <KpiCard
          icon={<Award className="h-4 w-4" />}
          label="NPS moyen catalogue"
          value={npsMoyen != null ? npsMoyen.toFixed(1) : '—'}
          subtext={`${totals.nps_count} évaluations`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-2">
        <KpiCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="CA réalisé (paiements)"
          value={formatDA(totals.ca_realise)}
        />
        <KpiCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="CA engagé (à encaisser)"
          value={formatDA(totals.ca_engage)}
        />
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <header className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <GraduationCap className="h-4 w-4" /> Sessions à venir (7 prochains jours)
          </h3>
          <Link to="/formation/sessions" className="text-xs text-muted-foreground hover:underline">
            Voir tout →
          </Link>
        </header>
        {upcomingSessions.length === 0 ? (
          <p className="text-xs text-muted-foreground">Aucune session sur 7 prochains jours.</p>
        ) : (
          <ul className="space-y-2">
            {upcomingSessions.slice(0, 10).map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/formation/sessions/${s.id}`}
                    className="line-clamp-1 font-medium hover:underline"
                  >
                    {s.reference} · {t(`formation.session.type.${s.type}`)}
                  </Link>
                  <div className="text-[10px] text-muted-foreground">
                    {formatDateDZ(s.date_debut)} → {formatDateDZ(s.date_fin)}
                  </div>
                </div>
                <Badge variant={statutVariant(s.statut)}>
                  {t(`formation.session.statut.${s.statut}`)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Top 5 formations par CA réalisé</h3>
        <ol className="space-y-1.5 text-xs">
          {stats
            .slice()
            .sort((a, b) => Number(b.ca_realise) - Number(a.ca_realise))
            .slice(0, 5)
            .map((s, i) => (
              <li
                key={s.formation_id}
                className="flex items-center justify-between gap-2 border-b border-border/40 py-1"
              >
                <span className="line-clamp-1">
                  <span className="mr-1 font-mono text-muted-foreground">#{i + 1}</span>
                  {s.titre}
                </span>
                <div className="flex items-center gap-3">
                  <span className="num">{formatDA(Number(s.ca_realise))}</span>
                  {s.nps_moyen != null && (
                    <Badge variant="outline" className="text-[10px]">
                      NPS {Number(s.nps_moyen).toFixed(1)}
                    </Badge>
                  )}
                </div>
              </li>
            ))}
        </ol>
      </section>
    </div>
  );
}

function statutVariant(
  s: SessionStatut,
): 'default' | 'success' | 'warning' | 'outline' | 'secondary' | 'destructive' {
  if (s === 'terminee') return 'success';
  if (s === 'en_cours') return 'default';
  if (s === 'annulee' || s === 'reportee') return 'destructive';
  if (s === 'complete') return 'warning';
  return 'outline';
}

function KpiCard({
  icon,
  label,
  value,
  subtext,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  subtext?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={cn('num text-xl font-semibold')}>{value}</div>
      {subtext && <div className="text-[10px] text-muted-foreground">{subtext}</div>}
    </div>
  );
}
